import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  Library,
  Compass,
  Store,
  Gamepad2,
} from "lucide-react";
import AuthModal from "./AuthModal";
import UserMenu from "./UserMenu";
import { AlertsPopover } from "./alerts/AlertsPopover";
import { useAuth } from "@/contexts/AuthContext";
import {
  platformLinks,
  platformGroups,
  isPlatformLinkActive,
} from "@/utils/platformNavigation";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState<"login" | "signup">("login");
  const linkClass = (active: boolean) =>
    "flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors " +
    (active
      ? "bg-holo-blue/15 text-sky-800 dark:text-sky-300"
      : "hover:bg-muted");
  return (
    <header className="platform-header border-b bg-background text-foreground">
      <div className="container max-w-7xl flex flex-wrap items-center justify-between gap-x-3 py-3">
        <Link
          to="/"
          aria-label="PokeLlama home"
          className="flex min-h-11 items-center gap-2 font-black text-xl tracking-tight"
        >
          <img
            src="/pokellama_logo.webp"
            alt=""
            className="h-9 w-9 rounded-full"
          />
          <span>
            PokeLlama
            <span className="hidden xl:block text-xs font-medium text-muted-foreground">
              Your Pokémon collection, connected.
            </span>
          </span>
        </Link>
        <nav
          id="platform-links"
          aria-label="PokeLlama platform"
          className={
            (open ? "block" : "hidden") +
            " order-3 basis-full lg:order-none lg:basis-auto lg:flex"
          }
        >
          <div
            data-platform-desktop
            className="hidden lg:flex items-center gap-1"
          >
            <Link
              to="/"
              aria-current={pathname === "/" ? "page" : undefined}
              className={linkClass(pathname === "/")}
            >
              Home
            </Link>
            {platformGroups.map((group, index) => {
              const active = group.links.some((link) =>
                isPlatformLinkActive(link.href, pathname),
              );
              const Icon = index === 0 ? Library : Compass;
              return (
                <DropdownMenu key={group.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-current={active ? "page" : undefined}
                      className={linkClass(active)}
                    >
                      <Icon aria-hidden="true" className="h-4 w-4" />
                      {group.label}
                      <ChevronDown aria-hidden="true" className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64 p-2">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {group.description}
                    </DropdownMenuLabel>
                    {group.links.map((link) => (
                      <DropdownMenuItem
                        key={link.href}
                        asChild
                        className="min-h-11"
                      >
                        <Link
                          to={link.href}
                          aria-current={
                            isPlatformLinkActive(link.href, pathname)
                              ? "page"
                              : undefined
                          }
                        >
                          {link.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
            <div className="ml-2 flex items-center gap-1 border-l pl-3">
              <Link
                to="/marketplace"
                aria-current={
                  isPlatformLinkActive("/marketplace", pathname)
                    ? "page"
                    : undefined
                }
                className={linkClass(
                  isPlatformLinkActive("/marketplace", pathname),
                )}
              >
                <Store aria-hidden="true" className="h-4 w-4" />
                Marketplace
              </Link>
              <Link
                to="/llama-lounge"
                aria-current={
                  isPlatformLinkActive("/llama-lounge", pathname)
                    ? "page"
                    : undefined
                }
                className={linkClass(
                  isPlatformLinkActive("/llama-lounge", pathname),
                )}
              >
                <Gamepad2 aria-hidden="true" className="h-4 w-4" />
                Llama Lounge
              </Link>
            </div>
          </div>
          <div
            data-platform-mobile
            className="lg:hidden space-y-3 border-t mt-3 pt-3 pb-2"
          >
            <Link
              to="/"
              aria-current={pathname === "/" ? "page" : undefined}
              onClick={() => setOpen(false)}
              className={linkClass(pathname === "/")}
            >
              Home
            </Link>
            {platformGroups.map((group) => (
              <section
                key={group.label}
                aria-label={group.label}
                className="rounded-xl border p-2"
              >
                <h2 className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </h2>
                {group.links.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    aria-current={
                      isPlatformLinkActive(link.href, pathname)
                        ? "page"
                        : undefined
                    }
                    onClick={() => setOpen(false)}
                    className={linkClass(
                      isPlatformLinkActive(link.href, pathname),
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
              </section>
            ))}
            <section
              aria-label="Shop and community"
              className="rounded-xl border p-2"
            >
              <h2 className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Shop & community
              </h2>
              {platformLinks.slice(6).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={
                    isPlatformLinkActive(link.href, pathname)
                      ? "page"
                      : undefined
                  }
                  onClick={() => setOpen(false)}
                  className={linkClass(
                    isPlatformLinkActive(link.href, pathname),
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </section>
          </div>
        </nav>
        <div className="flex shrink-0 items-center gap-1">
          {user && <AlertsPopover />}
          <UserMenu
            onAuthClick={(next = "login") => {
              setMode(next);
              setAuthOpen(true);
            }}
          />
          <button
            type="button"
            aria-label={
              open ? "Close platform navigation" : "Open platform navigation"
            }
            aria-expanded={open}
            aria-controls="platform-links"
            className="lg:hidden min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg hover:bg-muted"
            onClick={() => setOpen(!open)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        mode={mode}
        onModeChange={setMode}
      />
    </header>
  );
}
